import React, { useCallback, useEffect, useMemo, useState } from "react";

import { connect } from "react-redux";

import { Tooltip } from "antd";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import type { RootState } from "../../../redux/appState";

import {

  saveCatalogue,

  updateCatalogueDraft,

} from "../../../redux/coilConfigurator/actions";

import {

  buildStandardEquipmentCatalogueInput,

  removeCatalogueBottomEndFormRow,

  removeCatalogueCoilFrameFormRow,

  removeCataloguePowerSourceFormRow,

  type CatalogueBottomEndFormValues,

  type CatalogueCoilFrameFormValues,

  type CataloguePowerSourceFormValues,

  type StandardEquipmentCatalogueFormValues,

} from "../../../web-app";

import { catalogueToFormValues, formValuesEqual } from "../domain/catalogueForm";

import {

  defaultBottomEndValues,

  defaultCoilFrameValues,

  defaultPowerSourceValues,

  formatBottomEndCoilSection,

} from "../domain/catalogueModal";

import InfoAffordance from "../components/InfoAffordance";

import ConfirmDialog from "../components/ConfirmDialog";
import CcButton from "../components/CcButton";

import CatalogueItemModal, {

  type CatalogueItemKind,

  type CatalogueItemModalValues,

} from "./CatalogueItemModal";

import "./catalogue-panel.css";



interface CataloguePanelProps {

  savedCatalogue: RootState["coilConfigurator"]["catalogue"]["saved"];

  onDraftChange: (catalogue: ReturnType<typeof buildStandardEquipmentCatalogueInput>) => void;

  onSave: () => void;

}



interface CatalogueModalState {

  open: boolean;

  kind: CatalogueItemKind;

  mode: "create" | "edit";

  editIndex: number | null;

  initialValues: CatalogueItemModalValues;

  existingValues?: CatalogueItemModalValues;

}



const closedModalState = (): CatalogueModalState => ({

  open: false,

  kind: "powerSource",

  mode: "create",

  editIndex: null,

  initialValues: defaultPowerSourceValues(1),

});



const CatalogueActionButtons = ({

  editLabel,

  onEdit,

  onDelete,

}: {

  editLabel: string;

  onEdit: () => void;

  onDelete: () => void;

}) => (

  <div className="catalogue-actions-cell">

    <Tooltip title="Edit">

      <button type="button" className="catalogue-action-btn" onClick={onEdit} aria-label={editLabel}>

        <EditOutlined />

      </button>

    </Tooltip>

    <Tooltip title="Delete">

      <button

        type="button"

        className="catalogue-action-btn catalogue-action-btn--danger"

        onClick={onDelete}

        aria-label={`Delete ${editLabel.toLowerCase()}`}

      >

        <DeleteOutlined />

      </button>

    </Tooltip>

  </div>

);



const CataloguePanel = ({ savedCatalogue, onDraftChange, onSave }: CataloguePanelProps) => {

  const savedFormValues = useMemo(

    () => catalogueToFormValues(savedCatalogue),

    [savedCatalogue],

  );



  const [formValues, setFormValues] = useState<StandardEquipmentCatalogueFormValues>(savedFormValues);

  const [feedback, setFeedback] = useState<string | null>(null);

  const [feedbackIsError, setFeedbackIsError] = useState(false);

  const [modalState, setModalState] = useState<CatalogueModalState>(closedModalState);

  const [removeConfirm, setRemoveConfirm] = useState<{ open: boolean; onRemove: (() => void) | null }>({

    open: false,

    onRemove: null,

  });



  useEffect(() => {

    setFormValues(savedFormValues);

  }, [savedFormValues]);



  const isDirty = !formValuesEqual(formValues, savedFormValues);



  const applyFormValues = useCallback(

    (next: StandardEquipmentCatalogueFormValues) => {

      setFormValues(next);

      try {

        const built = buildStandardEquipmentCatalogueInput(next);

        onDraftChange(built);

        setFeedback(null);

        setFeedbackIsError(false);

      } catch (error) {

        const message = error instanceof Error ? error.message : "Invalid catalogue data";

        setFeedback(message);

        setFeedbackIsError(true);

      }

    },

    [onDraftChange],

  );



  const confirmRemove = (onRemove: () => void) => {

    setRemoveConfirm({ open: true, onRemove });

  };



  const openCreateModal = (kind: CatalogueItemKind) => {

    const rowNumber =

      kind === "powerSource"

        ? formValues.powerSources.length + 1

        : kind === "coilFrame"

          ? formValues.coilFrames.length + 1

          : formValues.bottomEnds.length + 1;



    const initialValues =

      kind === "powerSource"

        ? defaultPowerSourceValues(rowNumber)

        : kind === "coilFrame"

          ? defaultCoilFrameValues(rowNumber)

          : defaultBottomEndValues(rowNumber);



    setModalState({

      open: true,

      kind,

      mode: "create",

      editIndex: null,

      initialValues,

    });

  };



  const openEditModal = (

    kind: CatalogueItemKind,

    index: number,

    row: CatalogueItemModalValues,

  ) => {

    setModalState({

      open: true,

      kind,

      mode: "edit",

      editIndex: index,

      initialValues: { ...row },

      existingValues: { ...row },

    });

  };



  const closeModal = () => setModalState(closedModalState());



  const handleModalSubmit = (values: CatalogueItemModalValues) => {

    if (modalState.kind === "powerSource") {

      const row = values as CataloguePowerSourceFormValues;

      if (modalState.mode === "create") {

        applyFormValues({

          ...formValues,

          powerSources: [...formValues.powerSources, row],

        });

      } else if (modalState.editIndex !== null) {

        applyFormValues({

          ...formValues,

          powerSources: formValues.powerSources.map((existing, index) =>

            index === modalState.editIndex ? row : existing,

          ),

        });

      }

    } else if (modalState.kind === "coilFrame") {

      const row = values as CatalogueCoilFrameFormValues;

      if (modalState.mode === "create") {

        applyFormValues({

          ...formValues,

          coilFrames: [...formValues.coilFrames, row],

        });

      } else if (modalState.editIndex !== null) {

        applyFormValues({

          ...formValues,

          coilFrames: formValues.coilFrames.map((existing, index) =>

            index === modalState.editIndex ? row : existing,

          ),

        });

      }

    } else {

      const row = values as CatalogueBottomEndFormValues;

      if (modalState.mode === "create") {

        applyFormValues({

          ...formValues,

          bottomEnds: [...formValues.bottomEnds, row],

        });

      } else if (modalState.editIndex !== null) {

        applyFormValues({

          ...formValues,

          bottomEnds: formValues.bottomEnds.map((existing, index) =>

            index === modalState.editIndex ? row : existing,

          ),

        });

      }

    }

    closeModal();

  };



  const handleSave = () => {

    try {

      const built = buildStandardEquipmentCatalogueInput(formValues);

      onDraftChange(built);

      onSave();

      setFeedback("Catalogue saved.");

      setFeedbackIsError(false);

    } catch (error) {

      const message = error instanceof Error ? error.message : "Failed to save catalogue";

      setFeedback(message);

      setFeedbackIsError(true);

    }

  };



  return (

    <section className="catalogue-workspace panel-card">

      <form

        id="standard-equipment-catalogue-form"

        onSubmit={(event) => {

          event.preventDefault();

          handleSave();

        }}

      >

        <div className="panel-heading catalogue-panel__heading">

          <div className="panel-heading__left">

            <h2 className="panel-card__title">Standard Equipment Catalogue</h2>

          </div>

          <div className="button-row">

            <span className={`dirty-state${isDirty ? " dirty-state--unsaved" : ""}`}>

              {isDirty ? "Unsaved changes" : "Saved"}

            </span>

            <CcButton variant="primary" onClick={handleSave} disabled={!isDirty}>
              Save Catalogue
            </CcButton>

          </div>

        </div>



        {feedback && (

          <div

            className={feedbackIsError ? "options-feedback options-feedback-error" : "options-feedback"}

            role="alert"

          >

            {feedback}

          </div>

        )}



        <div className="catalogue-table-section">

          <div className="catalogue-table-header">

            <h3 className="catalogue-table-title">

              Power Sources <InfoAffordance glossaryKey="powerSource" />

            </h3>

            <CcButton variant="primary" onClick={() => openCreateModal("powerSource")}>
              + Add Power Source
            </CcButton>

          </div>

          <div className="table-scroll">

            <table className="catalogue-table catalogue-table--readonly" aria-label="Power sources catalogue">

              <thead>

                <tr>

                  <th scope="col">ID</th>

                  <th scope="col">Name</th>

                  <th scope="col">

                    Rating kVA <InfoAffordance glossaryKey="powerSourceRating" />

                  </th>

                  <th scope="col">

                    Frequency Hz <InfoAffordance glossaryKey="powerSourceFrequency" />

                  </th>

                  <th scope="col">

                    Max Voltage V <InfoAffordance glossaryKey="maximumPowerSourceVoltage" />

                  </th>

                  <th scope="col">

                    Max Current A <InfoAffordance glossaryKey="maximumPowerSourceCurrent" />

                  </th>

                  <th scope="col">Actions</th>

                </tr>

              </thead>

              <tbody>

                {formValues.powerSources.map((row, index) => (

                  <tr key={`ps-${index}-${row.id}`}>

                    <td>{row.id}</td>

                    <td>{row.name}</td>

                    <td>{row.powerSourceRatingKva}</td>

                    <td>{row.powerSourceFrequencyHz}</td>

                    <td>{row.maximumPowerSourceVoltageV}</td>

                    <td>{row.maximumPowerSourceCurrentA}</td>

                    <td>

                      <CatalogueActionButtons

                        editLabel="Edit power source"

                        onEdit={() => openEditModal("powerSource", index, row)}

                        onDelete={() =>

                          confirmRemove(() =>

                            applyFormValues(removeCataloguePowerSourceFormRow(formValues, index)),

                          )

                        }

                      />

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>



        <div className="catalogue-table-section">

          <div className="catalogue-table-header">

            <h3 className="catalogue-table-title">Coil Frames</h3>

            <CcButton variant="primary" onClick={() => openCreateModal("coilFrame")}>
              + Add Coil Frame
            </CcButton>

          </div>

          <div className="table-scroll">

            <table className="catalogue-table catalogue-table--readonly" aria-label="Coil frames catalogue">

              <thead>

                <tr>

                  <th scope="col">ID</th>

                  <th scope="col">Name</th>

                  <th scope="col">

                    Frame Size in <InfoAffordance glossaryKey="coilFrameSize" />

                  </th>

                  <th scope="col">

                    Inside Diameter mm <InfoAffordance glossaryKey="coilFrameInsideDiameter" />

                  </th>

                  <th scope="col">Actions</th>

                </tr>

              </thead>

              <tbody>

                {formValues.coilFrames.map((row, index) => (

                  <tr key={`cf-${index}-${row.id}`}>

                    <td>{row.id}</td>

                    <td>{row.name}</td>

                    <td>{row.coilFrameSizeInches}</td>

                    <td>{row.coilFrameInsideDiameterMm}</td>

                    <td>

                      <CatalogueActionButtons

                        editLabel="Edit coil frame"

                        onEdit={() => openEditModal("coilFrame", index, row)}

                        onDelete={() =>

                          confirmRemove(() =>

                            applyFormValues(removeCatalogueCoilFrameFormRow(formValues, index)),

                          )

                        }

                      />

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>



        <div className="catalogue-table-section">

          <div className="catalogue-table-header">

            <h3 className="catalogue-table-title">Bottom Ends</h3>

            <CcButton variant="primary" onClick={() => openCreateModal("bottomEnd")}>
              + Add Bottom End
            </CcButton>

          </div>

          <div className="table-scroll">

            <table className="catalogue-table catalogue-table--readonly" aria-label="Bottom ends catalogue">

              <thead>

                <tr>

                  <th scope="col">Name</th>

                  <th scope="col">

                    Turns <InfoAffordance glossaryKey="numberOfTurns" />

                  </th>

                  <th scope="col">

                    Contact Spacing mm <InfoAffordance glossaryKey="contactCenterSpacing" />

                  </th>

                  <th scope="col">

                    Bottom End Width mm <InfoAffordance glossaryKey="bottomEndWidth" />

                  </th>

                  <th scope="col">

                    Coil Section mm <InfoAffordance glossaryKey="coilSection" />

                  </th>

                  <th scope="col">Actions</th>

                </tr>

              </thead>

              <tbody>

                {formValues.bottomEnds.map((row, index) => (

                  <tr key={`be-${index}-${row.id}`}>

                    <td>{row.name}</td>

                    <td>{row.numberOfTurns}</td>

                    <td>{row.contactCenterSpacingsMm}</td>

                    <td>{row.bottomEndWidthMm}</td>

                    <td data-derived="coil-section">{formatBottomEndCoilSection(row)}</td>

                    <td>

                      <CatalogueActionButtons

                        editLabel="Edit bottom end"

                        onEdit={() => openEditModal("bottomEnd", index, row)}

                        onDelete={() =>

                          confirmRemove(() =>

                            applyFormValues(removeCatalogueBottomEndFormRow(formValues, index)),

                          )

                        }

                      />

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>

      </form>



      <CatalogueItemModal

        open={modalState.open}

        kind={modalState.kind}

        mode={modalState.mode}

        initialValues={modalState.initialValues}

        existingValues={modalState.existingValues}

        onClose={closeModal}

        onSubmit={handleModalSubmit}

      />



      <ConfirmDialog

        open={removeConfirm.open}

        title="Remove Row"

        message="Are you sure you want to remove this row? This cannot be undone."

        confirmLabel="Remove"

        onClose={() => setRemoveConfirm({ open: false, onRemove: null })}

        onConfirm={() => {

          removeConfirm.onRemove?.();

          setRemoveConfirm({ open: false, onRemove: null });

        }}

      />

    </section>

  );

};



const mapStateToProps = (state: RootState) => ({

  savedCatalogue: state.coilConfigurator.catalogue.saved,

});



const mapDispatchToProps = {

  onDraftChange: updateCatalogueDraft,

  onSave: saveCatalogue,

};



export default connect(mapStateToProps, mapDispatchToProps)(CataloguePanel);


